import _ from 'lodash'
import { InclusionType_PEnum, TranslationFieldType_PEnum } from '../../../configs/pseudoEnums'
import { TranslatableText } from '../../../utility/TranslatableText'

/**
 * The four condition slots the editor renders, empty.
 *
 * This list is a scaffold rather than data: the overlay looks each one up by type and reads
 * .text off whatever comes back, so the slots have to exist before anything is written into
 * them. (The note that used to sit here said the array should really be empty until a condition
 * is added, and that it affects too much of how TranslatableTextEditor works in the rates
 * overlay to change - still true.)
 */
const blankRateConditions = () => ([
    { type: 'Inclusion', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text), isChanged: false },
    { type: 'PaymentCancellation', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text), isChanged: false },
    { type: 'ChildPolicy', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text), isChanged: false },
    { type: 'TermsConditions', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text), isChanged: false }
])

const initialState = { 
    id: 0,
    profileId: '',
    rateConditions: blankRateConditions(),
    ratePricings: [],
    rateSeasons: [],
    currencyCode: 'ZAR',
    existsInDb: false,
    ratePricingConfigurations: [],
    hideNettRate: true,
    nettRate: null,
    rateExtraFeeItems: [],
    onlineRatesUrls: {},     //Dictionary: {profileItemType: Url}. This is to handle venues which have a different Url for each profileItem type.
    conditionsDraftJS: {},

    //Supplier rates: a profile can hold several rate periods. The fields above describe whichever
    //one is selected; these describe the set and what this user is allowed to do with it.
    //Rate period details. Validity is empty on rates that pre-date it and is required to publish.
    label: '',
    validFrom: null,
    validUntil: null,
    releaseDays: null,
    depositPercent: null,
    balanceDueDays: null,
    rateCancellationBands: [],
    rateBlackoutDates: [],
    rateDiscounts: [],

    ratePeriods: [],
    selectedRatePeriodId: 0,
    access: { canEdit: true, isWithdrawn: false, reason: null },
    auditTrail: [],

    //Set when an AI conversion has been loaded into the editor but not yet saved. The rates page
    //skips its reload while this is set, otherwise it would fetch over the unsaved draft.
    hasPendingExtraction: false,

    // Set when Add rates → Enter manually instead should open a blank editor for a new rate
    // sheet. The rates page skips its reload while this is set, otherwise it would fetch the
    // existing period back over the blank one. The list of saved periods stays as it is.
    editingNewRatePeriod: false,

    //What the AI conversion could not do. Shown in the editor so the gaps are visible while the
    //draft is being reviewed, then cleared once it is saved.
    extractionNotes: [],

    //Profile items deleted in this session that had rates against them.
    //
    //The rates are gone from the sheet by the time the supplier gets here, so there is nothing
    //left on the period to notice. Without this the columns simply are not there any more and
    //nobody is told why - which is the complaint behind TC-2771. Held against the profile rather
    //than the period, because the deletion applies to every sheet the item was priced on.
    deletedItemNotices: []
}

// Fields that describe the selected rate period. A load or a discard replaces these outright.
// Spreading a period over the previous one used to keep anything the payload left out, so an
// edit that was never saved — a price, a validity date, a cancellation band — survived closing
// the editor and showed on the preview.
const blankRatePeriod = () => ({
    id: 0,
    label: '',
    validFrom: null,
    validUntil: null,
    releaseDays: null,
    releaseNote: null,
    depositPercent: null,
    depositNote: null,
    depositIsNonRefundable: false,
    balanceDueDays: null,
    balanceDueNote: null,
    rateCancellationBands: [],
    rateBlackoutDates: [],
    rateDiscounts: [],
    ratePricings: [],
    rateSeasons: [],
    currencyCode: 'ZAR',
    existsInDb: false,
    ratePricingConfigurations: [],
    hideNettRate: true,
    nettRate: null,
    rateExtraFeeItems: [],
    onlineRatesUrls: {},
    sourceDocument: null,
    sourceDocumentId: null,
    sourceDocumentFileName: null
})

export const getNewRatePricingConfig = (configType, profileItemId, parentProfileItemId, isDummyElement = false, id = 0, isPricingFrom = false) => {
    return {
        id,
        profileRateId: 0,
        profileItemId,
        discountedValue: 0,
        isPricingFrom: isPricingFrom, 
        configType: configType ? configType : '',       //TODO: make enum
        isPP: false,            //isPP/isPPS: it can be neither, one or the other, but not both.
        isPPS: false,
        fromValue: '',          //Can include a '+' sign so varchar
        toValue: '',
        existsInDb: false,
        isMarkedForDeletion: false,
        isSelected: false,
        isDummyElement,         //To avoid the controlled/uncontrolled error.
        parentProfileItemId,
        gridSequence: 0,
        title: ''
    }
}

export const getNewRateExtraFeeItem = (id) => {
    return {
        id,
        itemName: '',
        price: null,
        inclusionType: InclusionType_PEnum.none
    }
}

/**
 * A blank offer. Sequence is set from its position when it is saved, so an offer added at the
 * bottom stays at the bottom - nothing else about an offer gives a reading order.
 */
let nextDiscountUid = 0

export const getNewRateDiscount = (id = 0) => {
    return {
        id,
        //A key that does not move when another offer is removed. An unsaved offer has no id to be
        //keyed on, and keying on the index makes React hand one offer's editor to another.
        uid: `discount-new-${++nextDiscountUid}`,
        profileRateId: 0,
        title: '',
        includedHtml: '',
        conditionsHtml: '',
        sequence: 0,
        existsInDb: false,
        isMarkedForDeletion: false
    }
}

/**
 * Whether a rich text body says anything.
 *
 * The editor does not hand back an empty string when it is empty - draft-js serialises an empty
 * document as "<p></p>" or "<p><br></p>", which is truthy. Taken at face value every offer the
 * supplier opened and did not fill in would be saved as a blank card on the rates preview.
 */
export const isRichTextEmpty = (html) => {
    if (!html) return true
    return html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length === 0
}

/**
 * Every language a translatable field has been written in, as plain strings.
 *
 * Offers and extras used to hold a string each. They hold a TranslatableText now, so a check
 * against the old field reads undefined and quietly calls a filled-in row empty.
 */
const textsOf = (field) => [field?.enText, ...(field?.translations ?? [])]
    .map(item => item?.stringValue)
    .filter(value => typeof value === 'string')

/** True when a translatable field says something in any language. */
export const hasAnyTranslatedText = (field) => textsOf(field).some(value => !!value.trim())

/** The same for a field the rich text editor writes, where empty is markup rather than ''. */
export const hasAnyTranslatedRichText = (field) => textsOf(field).some(value => !isRichTextEmpty(value))

/** An offer with no title and nothing in either body was opened and abandoned, so it is not saved. */
export const isDiscountEmpty = (discount) => (
    !hasAnyTranslatedText(discount?.titleText)
    && !hasAnyTranslatedRichText(discount?.included)
    && !hasAnyTranslatedRichText(discount?.conditions)
)

export const getNewRatePricing = (rateSeasonId, ratePricingConfigurationId) => {
    return {
        rateSeasonId, 
        ratePricingConfigurationId,
        id: 0,      //This can just remain 0 on the UI if !existsInDb because it is not used (we use rateSeasonId and rateConfigId to identify).
        existsInDb: false,
        isMarkedForDeletion: false
    }
}

const ratesReducer = (state = initialState, action) => {
    switch (action.type) {
        case 'SET_PROFILE_ID':
            return {...state, profileId: action.data }
        case 'SET_PENDING_EXTRACTION':
            return { ...state, hasPendingExtraction: action.data }
        case 'SET_EDITING_NEW_RATE_PERIOD':
            return { ...state, editingNewRatePeriod: action.data }
        case 'START_NEW_RATE_PERIOD':
            // A new sheet, typed in by hand. The periods already saved stay in the list so Close
            // can put the one on screen back, and so this one is only added once it is saved.
            return {
                ...state,
                ...blankRatePeriod(),
                currencyCode: state.currencyCode || 'ZAR',
                profileId: state.profileId,
                rateConditions: blankRateConditions(),
                conditionsDraftJS: {},
                extractionNotes: [],
                hasPendingExtraction: false,
                editingNewRatePeriod: true
            }
        case 'SET_EXTRACTION_NOTES':
            return { ...state, extractionNotes: action.data ?? [] }
        case 'ADD_DELETED_ITEM_NOTICE': {
            //Keyed on the item so deleting the same thing twice - which a double click will do -
            //does not stack two identical bars on the preview.
            const notices = state.deletedItemNotices ?? []
            if (notices.some(notice => notice.id === action.data?.id)) return state
            return { ...state, deletedItemNotices: [...notices, action.data] }
        }
        case 'DISMISS_DELETED_ITEM_NOTICE':
            return {
                ...state,
                deletedItemNotices: (state.deletedItemNotices ?? []).filter(notice => notice.id !== action.data)
            }
        case 'SET_RATE_PERIOD_DETAILS':
            return { ...state, ...action.data }
        case 'SET_RATE_CANCELLATION_BANDS':
            return { ...state, rateCancellationBands: action.data }
        case 'SET_RATE_BLACKOUT_DATES':
            return { ...state, rateBlackoutDates: action.data }
        case 'SET_RATE_DISCOUNTS':
            return { ...state, rateDiscounts: action.data ?? [] }
        case 'SET_RATE_PERIODS':
            return { ...state, ratePeriods: action.data }
        case 'SELECT_RATE_PERIOD':
            return { ...state, selectedRatePeriodId: action.data }
        case 'SET_RATES_ACCESS':
            return { ...state, access: action.data }
        case 'SET_RATES_AUDIT':
            return { ...state, auditTrail: action.data }
        case 'UPSERT_RATE_PERIOD': {
            // The saved period, kept apart from the copy the editor mutates, so Close can put
            // this one back. A new id replaces the unsaved placeholder it grew out of.
            const savedPeriod = action.data
            if (!savedPeriod?.id) return state
            const periods = [...(state.ratePeriods ?? [])]
            const index = periods.findIndex(x => x.id === savedPeriod.id)
            if (index >= 0) {
                periods[index] = savedPeriod
            } else {
                const unsaved = periods.findIndex(x => !x.id)
                if (unsaved >= 0) periods[unsaved] = savedPeriod
                else periods.push(savedPeriod)
            }
            return { ...state, ratePeriods: periods, selectedRatePeriodId: savedPeriod.id }
        }
        case 'SET_RATES_DATA': {
            const incoming = action.data ?? {}
            const next = {
                ...state,
                ...blankRatePeriod(),
                ...incoming,
                // A period payload must not wipe the list, the access block, or the audit trail.
                ratePeriods: state.ratePeriods,
                selectedRatePeriodId: state.selectedRatePeriodId,
                access: state.access,
                auditTrail: state.auditTrail,
                hasPendingExtraction: state.hasPendingExtraction,
                extractionNotes: state.extractionNotes,
                conditionsDraftJS: {}
            }
            /*
             * Conditions are merged into the four slots, never replaced by what came back.
             *
             * The editor sends only the conditions that changed, so a save that touched none of
             * them returns an empty list. Spread over the state above, that read as "there are no
             * conditions", wiped the slots, and left the overlay reading .text off undefined the
             * moment any term was ticked:
             *
             *   TypeError: Cannot read properties of undefined (reading 'text')
             *
             * An empty list means nothing changed, not that nothing exists.
             */
            const mergedConditions = state.rateConditions?.length > 0
                ? _.cloneDeep(state.rateConditions)
                : blankRateConditions()

            ;(incoming.rateConditions ?? []).forEach(condition => {
                const slot = mergedConditions.find(c => c.type === condition.type)
                if (!slot) return
                slot.text = condition.text
                slot.id = condition.id
                slot.existsInDb = condition.existsInDb
            })
            mergedConditions.forEach(condition => { condition.isMarkedForDeletion = false })
            next.rateConditions = mergedConditions
            next.ratePricingConfigurations = next.ratePricingConfigurations ?? []
            next.ratePricings = next.ratePricings ?? []
            next.rateSeasons = next.rateSeasons ?? []

            return next
        }
        case 'SET_RATE_PRICINGS':
            return {...state, ratePricings: action.data }
        case 'SET_RATE_CONDITIONS':
            const conditionsPayload = action.data
            if (conditionsPayload?.length > 0) {
                const stateConditionsCopy = _.cloneDeep(state.rateConditions)
                conditionsPayload.forEach(condition => {
                    const conditionInCurrentList = stateConditionsCopy.find(c => c.type === condition.type)
                    conditionInCurrentList.text = condition.text
                    conditionInCurrentList.isChanged = condition.isChanged
                    conditionInCurrentList.isMarkedForDeletion = condition.isMarkedForDeletion
                    conditionInCurrentList.existsInDb = condition.existsInDb
                    conditionInCurrentList.id = condition.id
                })
                return {...state, rateConditions: stateConditionsCopy }
            }
            return state
        case 'SET_PROFILE_RATE_SEASONS':
            return {...state, rateSeasons: action.data }
        case 'SET_SEASON_RANGES':
            return {...state, season:{...state.season, dateRanges: action.data }}
        case 'DELETE_SEASON':
            const seasonsClone = _.cloneDeep(state.rateSeasons)
            const ratePricingsClone = _.cloneDeep(state.ratePricings)
            return {...state, rateSeasons: seasonsClone.filter(season => season.id !== action.data), rates: ratePricingsClone.filter(rate => rate.RateSeasonId !== action.data)}
        case 'SET_RATES_PRICING_OPTIONS':
            return {...state, pricingOptions: action.data }
        case 'SET_HIDE_NET_RATE':
            return {...state, hideNettRate: action.data }
        case 'SET_NETT_RATE':
            return {...state, nettRate: action.data }
        case 'SET_EXTRA_FEES':
            return {...state, rateExtraFeeItems: action.data }
        case 'SET_CURRENCY_CODE':
            return {...state, currencyCode: action.data }
        case 'SET_RATES_URL':
            return {...state, onlineRatesUrls: action.data }
        case 'SET_CONDITIONS_DRAFTJS':
            const currentConditionsDraftJS = _.cloneDeep(state.conditionsDraftJS)
            return {...state, conditionsDraftJS: {...currentConditionsDraftJS, ...action.data}}
        case 'REMOVE_RATE_PERIOD': {
            // The editor stays open until this lands, then closes onto whatever is left. Dropping
            // the period here — rather than after the preview has already painted — is what stops
            // the deleted rates showing for a moment on the way out.
            const ratePeriodId = action.data?.ratePeriodId
            const periods = (state.ratePeriods ?? []).filter(period => period.id !== ratePeriodId)
            if (!periods.length) {
                return {
                    ...initialState,
                    profileId: action.data?.profileId || state.profileId,
                    access: state.access,
                    auditTrail: state.auditTrail,
                    rateConditions: blankRateConditions()
                }
            }
            const nextPeriod = periods[0]
            // Cloned, same as switching periods by hand: the editor writes this copy, and the
            // one in the list is what Close puts back.
            return ratesReducer(
                { ...state, ratePeriods: periods, selectedRatePeriodId: nextPeriod.id },
                { type: 'SET_RATES_DATA', data: _.cloneDeep(nextPeriod) }
            )
        }
        case 'RESET_RATES':
            const defaultConditions = [
                { type: 'Inclusion', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text) }, 
                { type: 'PaymentCancellation', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text) }, 
                { type: 'ChildPolicy', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text) }, 
                { type: 'TermsConditions', text: TranslatableText.getNew(false, '', TranslationFieldType_PEnum.Text) }
            ]
            return {...initialState, profileId: action.data, rateConditions: defaultConditions}
        case 'SET_RATE_PRICING_CONFIGURATIONS':
            return {...state, ratePricingConfigurations: action.data ?? [] }
        default:
            return state
    }
}

export default ratesReducer